import * as React from 'react'
import { ParsedUrlQuery } from 'querystring'
import { useRouter } from 'next/router'
import { NextSeo } from 'next-seo'
import * as Types from '../../../../../declarations'
import * as RedisUtil from 'lib/redis'
import PageTemplate from '../../../../../components/PageTemplate'
import { Alert, Button, Table, Title, Blockquote } from '@mantine/core'
import Link from 'next/link'
import { sluggify } from 'lib/helper'
import pluralize from 'pluralize'

import { withVersion } from '../../../../../lib/withVersion'
import Logger from 'lib/logger'

interface TrekPageProps {
	pageData: Types.App.Types.BestGuidesPageData
	version: Types.VERSION
}

/**
 * Visually identical replacement for read-only Mantine <Rating fractions={4}>
 * (same star path, colours, sizes and quarter-star rounding) that renders one
 * compact SVG instead of 21 stacked label/star nodes (~9KB of HTML each —
 * ~1.3MB across this page's ratings).
 */
const RATING_STAR_PATH =
	'M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z'

const SlimRating = ({
	value,
	size = 'sm',
}: {
	value: number
	size?: 'sm' | 'xl'
}) => {
	const id = React.useId()
	const starSize = size === 'xl' ? 28 : 18 // matches Mantine --rating-size sm/xl

	// Mantine Rating rounds to quarter-star fractions
	const rounded = Math.round(value * 4) / 4
	const starFractions = [0, 1, 2, 3, 4].map((star) =>
		Math.min(Math.max(rounded - star, 0), 1),
	)

	const filledColour = 'var(--mantine-color-brand-filled)'
	const emptyColour = 'var(--mantine-color-gray-3)'

	const partialStar = starFractions.findIndex((f) => f > 0 && f < 1)

	return (
		<div style={{ display: 'flex', width: 'max-content' }}>
			<svg
				width={starSize * 5}
				height={starSize}
				viewBox="0 0 120 24"
				xmlns="http://www.w3.org/2000/svg"
			>
				<defs>
					<path
						id={id}
						d={RATING_STAR_PATH}
						strokeLinecap="round"
						strokeLinejoin="round"
					/>
					{partialStar !== -1 && (
						<clipPath id={`${id}-clip`}>
							<rect
								x={partialStar * 24}
								y={0}
								width={24 * starFractions[partialStar]}
								height={24}
							/>
						</clipPath>
					)}
				</defs>
				{/* empty star base row */}
				<g style={{ fill: emptyColour, stroke: emptyColour }}>
					{[0, 1, 2, 3, 4].map((star) => (
						<use key={star} href={`#${id}`} x={star * 24} />
					))}
				</g>
				{/* filled stars on top */}
				<g style={{ fill: filledColour, stroke: filledColour }}>
					{starFractions.map(
						(fraction, star) =>
							fraction === 1 && (
								<use key={star} href={`#${id}`} x={star * 24} />
							),
					)}
				</g>
				{/* partially filled star */}
				{partialStar !== -1 && (
					<g
						style={{ fill: filledColour, stroke: filledColour }}
						clipPath={`url(#${id}-clip)`}
					>
						<use href={`#${id}`} x={partialStar * 24} />
					</g>
				)}
			</svg>
		</div>
	)
}

const BestGuidesPage = ({ pageData, version }: TrekPageProps) => {
	const router = useRouter()

	const seo = {
		title: `Best ${pageData.name} Climbing Guides 2025 - ${
			version === Types.VERSION.TREKKING ? 'Guided Trekking' : 'Guided Peaks'
		}`,
		description: `Find the best ${pageData.name} guides. Compare ${pageData.guideCount} guide companies with ${pageData.matchingReviews.length} climber reviews. Trip analysis and ratings to help you choose the right guide.`,
		canonical: `${router.asPath}`,
		openGraph: {
			title: `Best ${pageData.name} Climbing Guides 2025`,
			description: `Find the best ${pageData.name} guides. Compare ${pageData.guideCount} guide companies with ${pageData.matchingReviews.length} climber reviews.`,
		},
	}

	return (
		<PageTemplate>
			<NextSeo {...seo} />
			<div className="max-w-4xl mx-auto px-4 py-8">
				<Title order={1} mb="xl">
					Best {pageData.name} Climbing Guides 2025
				</Title>
				<div className="bg-gray-50 p-6 rounded-lg mb-8">
					<p className="text-lg mb-4">
						Looking for the best guides to climb {pageData.name}? We&apos;ve
						analyzed {pageData.guideCount} guide companies
						{pageData.matchingReviews.length > 0 && (
							<>
								{' '}
								and their {pageData.matchingReviews.length} reviews to help you
								make the best choice
							</>
						)}
						.
					</p>
				</div>

				<Alert
					radius="md"
					mb="xl"
					styles={(theme) => ({
						root: {
							padding: '16px 24px',
						},
						message: {
							'& > div': {
								[`@media (max-width: calc(48em - 1px))`]: {
									display: 'flex',
									flexDirection: 'column',
									gap: '16px',
								},
								[`@media (min-width: 48em)`]: {
									display: 'flex',
									justifyContent: 'space-between',
									alignItems: 'center',
								},
							},
						},
					})}
				>
					<div>
						<div>
							<div style={{ fontSize: 22, marginBottom: 8 }}>
								Want to see all {pageData.guideCount} guides for {pageData.name}
								?
							</div>
							<div style={{ fontSize: 16 }}>
								View complete guide listings with detailed pricing, trip
								specifics, and guide-specific information.
							</div>
						</div>
						<Link
							href={`/${router.query.routeType}/${router.query.continent}/${router.query.countries}/${router.query.slug}#guides`}
							className="plausible-event-name=All-Guides-Link-Click"
						>
							<Button variant="filled">
								View all {pageData.name} Guides →
							</Button>
						</Link>
					</div>
				</Alert>

				{/* Top Guides Section */}
				<section className="mb-12">
					<Title order={2} mb="xl">
						Top-Rated Guide Companies
					</Title>
					<Table
						verticalSpacing="md"
						fz={'lg' as const}
						styles={(theme) => ({
							td: {
								'& .rating-wrapper': {
									display: 'flex',
									flexDirection: 'column',
									gap: '8px',
									[`@media (min-width: 48em)`]: {
										flexDirection: 'row',
										alignItems: 'center',
										gap: '16px',
									},
								},
							},
						})}
					>
						<thead>
							<tr>
								{/* <th style={{ width: 50 }}>#</th> */}
								<th>Company</th>
								<th style={{ width: 240 }}>Rating</th>
								<th style={{ width: 160 }}>Reviews</th>
							</tr>
						</thead>
						<tbody>
							{pageData.topGuides.map((guide, index) => {
								return (
									<tr key={guide.companyId}>
										{/* <td style={{ fontSize: 18, fontWeight: 500 }}>
											{index + 1}
										</td> */}
										<td style={{ fontSize: 18, fontWeight: 500 }}>
											<Link
												href={`/guides/${sluggify(guide.companyName)}`}
												style={{
													color: 'inherit',
													textDecoration: 'none',
												}}
												className="hover:underline"
											>
												{guide.companyName}
												{guide.isPremium && (
													<span
														style={{
															color: 'gold',
															marginLeft: '4px',
														}}
														title="Premium Guide"
													>
														★
													</span>
												)}
											</Link>
										</td>
										<td>
											<div
												style={{
													display: 'flex',
													alignItems: 'center',
													gap: '16px',
													flexWrap: 'wrap',
												}}
											>
												<div
													style={{
														padding: '4px 12px',
														background: '#e6f4ea',
														color: '#1e7e34',
														borderRadius: '16px',
														fontWeight: 500,
													}}
												>
													{guide.reviewScore}%
												</div>
												<SlimRating value={guide.reviewScore / 20} />
											</div>
										</td>
										<td style={{ color: '#666' }}>
											{guide.totalReviewCount.toLocaleString()}{' '}
											{pluralize('review', guide.totalReviewCount)}
										</td>
									</tr>
								)
							})}
						</tbody>
					</Table>
				</section>
				{/* Reviews Section */}
				{pageData.matchingReviews.length > 0 && (
					<>
						<section className="mb-12">
							<Title order={2} mb="xl" mt={48}>
								{pageData.name} Climber Reviews
							</Title>
							<div className="space-y-6">
								{pageData.matchingReviews.map((review) => {
									const highlightedText = review.reviewText.replace(
										new RegExp(pageData.name, 'gi'),
										`<strong>${pageData.name}</strong>`,
									)
									return (
										<div
											key={`${review.companyId}-${review.reviewText.slice(
												0,
												20,
											)}`}
											className="border rounded-lg p-6"
										>
											<div
												style={{
													marginBottom: '16px',
													marginTop: '32px',
												}}
											>
												<Title order={3} mb={0}>
													<Link
														href={`/guides/${sluggify(review.companyName)}`}
														style={{
															color: 'inherit',
															textDecoration: 'none',
														}}
														className="hover:underline"
													>
														{review.companyName}
													</Link>
												</Title>
												<div
													style={{
														display: 'flex',
														alignItems: 'center',
														gap: '16px',
													}}
												>
													<SlimRating
														value={review.reviewScore / 20}
														size="xl"
													/>
													<div style={{ color: '#666', fontSize: '14px' }}>
														via{' '}
														{review.provider.charAt(0).toUpperCase() +
															review.provider.slice(1)}
													</div>
												</div>
											</div>
											<Blockquote style={{ marginBottom: 0 }}>
												<span
													dangerouslySetInnerHTML={{ __html: highlightedText }}
												/>
											</Blockquote>
										</div>
									)
								})}
							</div>
						</section>
					</>
				)}
			</div>
		</PageTemplate>
	)
}

interface TrekPageQuery extends ParsedUrlQuery {
	routeType?: string
	slug?: string
}

export const getServerSideProps = withVersion(async (context) => {
	const { routeType, slug, guidesSlug } = context.params as TrekPageQuery

	// todo: merge these two 404 checks in to one (route type and slug check), and maybe even the third - no trip found
	if (!(routeType === 'trek' || routeType === 'climbing')) {
		Logger.warn('BestGuidesPage: invalid route type', { routeType })
		// 404?
		return {
			notFound: true,
		}
	}

	if (!slug || guidesSlug?.[0] !== `best-${slug}-climbing-guides-2025`) {
		Logger.warn('NO SLUG', { guidesSlug: guidesSlug?.[0] })
		// 404
		return {
			notFound: true,
		}
	}

	const peaksData = await RedisUtil.Data.PeaksWithTopGuides.get(
		{
			version: context.version,
		},
		// todo: coment this back out
		// false,
		// true,
	)

	const matchingPeak = peaksData.find((peak) => peak.slug === slug)

	if (!matchingPeak) {
		Logger.warn('Peak not found in top guides data', { slug })
		return {
			notFound: true,
		}
	}

	return {
		props: {
			pageData: matchingPeak,
			version: context.version,
		},
	}
})

export default BestGuidesPage
